package one.devsbox.www.ui.users.detail

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.EditOutline
import compose.assets.svg.ResetOutline
import compose.assets.svg.TrashOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.ads.TargetType
import one.devsbox.shared.ads.UserAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.PermissionWrapperEditor
import one.devsbox.www.ui.users.color
import one.devsbox.www.ui.users.*
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelField
import one.devsbox.www.ui.widgets.LabelSection
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun UserDUI() {
    with(UserDCtx.get()) {
        ContentUserDUI()
    }
}

@Composable
context(ctx: UserDCtx)
private fun ContentUserDUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val data = state.nnvl
                    SuccessUI(data)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }
    }

    ctx.vm.infoDialogMessage?.let { infoMsg ->
        AlertDialog(
            onDismissRequest = { ctx.action.dismissInfoDialog() },
            title = { Text(stringResource(Res.string.ui_users_detail_info_dialog_title)) },
            text = { Text(stringResource(infoMsg)) },
            confirmButton = {
                TextButton(onClick = { ctx.action.dismissInfoDialog() }) {
                    Text(stringResource(Res.string.ui_users_edit_btn_ok))
                }
            }
        )
    }

    if (ctx.vm.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteConfirmation() },
            title = { Text(stringResource(Res.string.ui_users_detail_delete_confirm_title)) },
            text = { Text(stringResource(Res.string.ui_users_detail_delete_confirm_message)) },
            confirmButton = {
                TextButton(onClick = { ctx.action.confirmDelete() }) {
                    Text(
                        text = stringResource(Res.string.ui_users_detail_delete_btn_confirm),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { ctx.action.dismissDeleteConfirmation() }) {
                    Text(
                        text = stringResource(Res.string.ui_users_detail_delete_btn_dismiss),
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        )
    }
}

@Composable
context(ctx: UserDCtx)
private fun SuccessUI(data: UserDVM.Data) {
    val user = data.user
    val permissions = data.permissions

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isWide = maxWidth >= 1000.dp
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
                .verticalScroll(rememberScrollState())
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = user.name,
                    style = MaterialTheme.typography.headlineLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Column(horizontalAlignment = Alignment.End) {
                    LabelField(text = stringResource(Res.string.ui_users_listing_header_activity))
                    Text(
                        text = user.lastActivity(),
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Box(modifier = Modifier.weight(2f)) {
                        PermissionsMatrixSection(permissions)
                    }
                    Box(modifier = Modifier.weight(1f)) {
                        RecentAccessLogsSection(data.logs)
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    PermissionsMatrixSection(permissions)
                    RecentAccessLogsSection(data.logs)
                }
            }

            Spacer(modifier = Modifier.height(250.dp))
        }
    }
}

@Composable
private fun PermissionsMatrixSection(permissions: PermissionWrapperEditor) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_users_detail_title_permissions_matrix))

            Spacer(modifier = Modifier.height(16.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            // Table Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(modifier = Modifier.weight(1.5f)) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_category))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_view))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_edit))
                }
                Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                    LabelField(text = stringResource(Res.string.ui_users_detail_header_delete))
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            PermissionRow(stringResource(Res.string.ui_users_detail_cat_media), permissions.media)
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            PermissionRow(
                stringResource(Res.string.ui_users_detail_cat_campaigns),
                permissions.campaign
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            PermissionRow(
                stringResource(Res.string.ui_users_detail_cat_devices),
                permissions.device
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            PermissionRow(
                stringResource(Res.string.ui_users_detail_cat_locations),
                permissions.location
            )
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            PermissionRow(stringResource(Res.string.ui_users_detail_cat_users), permissions.user)
        }
    }
}

@Composable
context(ctx: UserDCtx)
private fun RecentAccessLogsSection(logs: List<SharedIO.UserActivity>) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_users_detail_title_recent_logs))
            Spacer(modifier = Modifier.height(16.dp))

            if (logs.isEmpty()) {
                Text(
                    text = stringResource(Res.string.ui_users_activity_empty),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            } else {
                logs.sortedByDescending { it.dt }.take(5).forEach { log ->
                    val action = UserAction[log.action]
                    val target = TargetType[log.target]
                    val dotColor = action?.color() ?: MaterialTheme.colorScheme.outline
                    val actionText = action?.let { stringResource(it.text()) } ?: "ACTION"
                    val targetText =
                        target?.let { stringResource(it.text(log.idTarget)) } ?: "TARGET"
                    LogItem(
                        title = "$actionText $targetText",
                        subtitle = "${log.name} • ${log.dt.formatAsLastActivity()}",
                        dotColor = dotColor
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))
            OutlinedButton(
                onClick = { ctx.action.viewAuditTrail() },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(4.dp),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
            ) {
                Text(
                    stringResource(Res.string.ui_users_detail_btn_audit_trail),
                    color = MaterialTheme.colorScheme.primary,
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
private fun PermissionRow(label: String, item: PermissionWrapperEditor.Item) {
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                modifier = Modifier.weight(1.5f),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            PermissionCheck(item.read.state, modifier = Modifier.weight(1f))
            PermissionCheck(item.write.state, modifier = Modifier.weight(1f))
            PermissionCheck(item.delete.state, modifier = Modifier.weight(1f))
        }
    }
}

@Composable
private fun PermissionCheck(checked: Boolean, modifier: Modifier = Modifier) {
    Box(modifier = modifier) {
        Checkbox(
            checked = checked,
            enabled = false,
            onCheckedChange = {},
            modifier = Modifier.align(Alignment.Center),
            colors = CheckboxDefaults.colors(
                disabledCheckedColor = MaterialTheme.colorScheme.primary,
                disabledUncheckedColor = MaterialTheme.colorScheme.outline,
                disabledIndeterminateColor = MaterialTheme.colorScheme.primary,
            )
        )
    }
}

@Composable
private fun LogItem(title: String, subtitle: String, dotColor: Color) {
    Row(modifier = Modifier.padding(vertical = 12.dp)) {
        Box(
            modifier = Modifier
                .padding(top = 6.dp)
                .size(6.dp)
                .background(dotColor)
        )
        Spacer(modifier = Modifier.width(12.dp))
        Column {
            Text(
                text = title,
                style = MaterialTheme.typography.bodySmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Text(
                text = subtitle,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
